import warningIcon from '@disy/cadenza-icons/warning.svg';
import h from 'hyperscript';
import groupBy from 'lodash-es/groupBy';
import partition from 'lodash-es/partition';

import { createIconButton } from 'ui/button/button';
import { createMoreActionsButton } from 'ui/button/more-actions-button';
import type { MenuContent } from 'ui/menu/menu';
import { setupMenuButton } from 'ui/menu/menu';
import { setupPopupButton } from 'ui/popup/popup';
import { createResourceLabel } from 'ui/resource-label/resource-label';

import { isFeatureAvailable } from 'cadenza/features';
import { AttributeCategory } from 'cadenza/repository/attribute-category';
import type { ObjecttypeIdString } from 'cadenza/repository/model/objecttype-id';
import type { AttributePickerAttribute } from 'cadenza/ui/attribute-picker/attribute-picker';
import type { AttributeTileOptions } from 'cadenza/ui/attribute-tile/attribute-tile';
import { attributeTile } from 'cadenza/ui/attribute-tile/attribute-tile';
import type { AttributeTileAttribute } from 'cadenza/ui/attribute-tile/attribute-tile-attribute';
import { icon } from 'cadenza/utils/icon/icon';
import { uniqueId } from 'cadenza/utils/unique-id';
import { renderAttributesAndHierarchies } from 'cadenza/workbook/attribute-and-hierarchy-list-renderer/attribute-and-hierarchy-list-renderer';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import type { OutdatedEnrichmentsContext } from 'cadenza/workbook/workbook-panel/databrowser/outdated-enrichments-context';
import { createEnrichmentOutdatedAlert } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-summary/enrichment-summary';

import './attribute-list.css';

export type CreateAttributeListFunction = (attributeItems: HTMLElement[], labelId: string) => HTMLElement;
export type AttributeTileOptionsProvider = (attribute: AttributeTileAttribute) => AttributeTileOptions;

export interface CreateAttributeListGroupedByCategoryOptions {
  acceptedKeywords?: string[];
  showBoundToWorkbookHint?: boolean;
  outdatedEnrichmentsContext?: OutdatedEnrichmentsContext;
  hoverContext?: boolean;
  enrichmentObjecttypeIds?: ObjecttypeIdString[];
  openEnrichmentSections?: Set<string>;
  withManagementLink?: boolean;
  /** Function to create the individual attribute lists per attribute group. */
  createAttributeList: CreateAttributeListFunction;
  getAttributeActions?: FurtherActionsProvider;
  getEnrichmentActions?: (enrichmentObjecttypeId: ObjecttypeIdString) => ReturnType<MenuContent>;
  getAttributeById: (attributeId: AttributeId) => AttributePickerAttribute;

  /**
   * When this is present, this group will not be hidden when the attributes
   * list is empty. Instead, an info alert will be displayed with the given text.
   */
  emptyGroupText?: string;

  /** Any options given here will override the standard options. These options will be applied to every tile in the list. */
  attributeTileOptions?: Pick<AttributeTileOptions, 'noDetails'>;
  /** Takes precedence over other options. Allows for adjusting options to specific tile. */
  attributeTileOptionsProvider?: AttributeTileOptionsProvider;
}

export type FurtherActionsProvider = (attribute: AttributeTileAttribute['id']) => ReturnType<MenuContent>;

/**
 * Creates list of attributes grouped by attribute type (measure/dimension).
 *
 * @param attributes - The entries to show in this list
 * @param options - Additional settings.
 * @return - the attribute elements.
 */
export function createAttributeListGroupedByCategory(
  attributes: AttributePickerAttribute[],
  options: CreateAttributeListGroupedByCategoryOptions,
) {
  if (!attributes.length && options.emptyGroupText) {
    return [h('d-alert', { type: 'info', size: 's' }, options.emptyGroupText)];
  }
  return partition(attributes, ({ category }) => category === 'MEASURE')
    .reverse() // List dimensions (and hierarchies without heading) first
    .filter((attrs) => attrs.length > 0)
    .map((filteredAttributes, _, partitions) =>
      createAttributeItemList(filteredAttributes, options, partitions.length > 1),
    );
}

function createAttributeItemList(
  attributes: AttributePickerAttribute[],
  options: CreateAttributeListGroupedByCategoryOptions,
  showCategoryLabels: boolean,
) {
  const { showBoundToWorkbookHint, createAttributeList } = options;
  const labelId = uniqueId();
  const formGroupContent = [];
  if (showCategoryLabels) {
    const category = attributes[0].category; // All attributes are of the same category.
    formGroupContent.push(h('label', { id: labelId }, AttributeCategory.i18n(category, { count: 3 })));
  }

  const [enrichedAttributes, notEnrichedAttributes] = partition(attributes, (a) => a.enrichedObjecttypeId!);
  if (notEnrichedAttributes.length) {
    const attributeItems = renderAttributeItems(notEnrichedAttributes, options);
    const attributeList = createAttributeList(attributeItems, labelId);
    attributeList.setAttribute('aria-labelledby', labelId);
    formGroupContent.push(attributeList);
  }

  if (enrichedAttributes.length) {
    const enrichmentObjecttypeIds = new Set(options.enrichmentObjecttypeIds ?? []);
    const attributesByEnrichment = groupBy(enrichedAttributes, (attribute) => attribute.objecttypeId);
    for (const [enrichmentId, attributesOfEnrichmentObjecttype] of Object.entries(attributesByEnrichment)) {
      const attributesOfEnrichmentObjecttypeWithBoundStatus = attributesOfEnrichmentObjecttype.map((attr) => {
        let objecttypeBoundToWorkbook;
        if (showBoundToWorkbookHint) {
          objecttypeBoundToWorkbook = true;
        }
        return { ...attr, objecttypeBoundToWorkbook };
      });
      const items = renderAttributeItems(attributesOfEnrichmentObjecttypeWithBoundStatus, options);
      const enrichedAttributeList = createAttributeList(items, labelId);
      const enrichmentObjecttypeId = enrichmentId as ObjecttypeIdString;
      if (enrichmentObjecttypeIds?.has(enrichmentObjecttypeId)) {
        const enrichmentPrintName = attributesOfEnrichmentObjecttype[0].objecttypePrintName ?? '';
        const groupEnrichmentSummaryChildren = createGroupEnrichmentSummaryChildren(
          enrichmentPrintName,
          enrichmentObjecttypeId,
          options,
        );
        const groupedEnrichment = h(
          'details.d-panel-section.d-panel-section-small.enrichment-group-tile',
          {
            open: options.openEnrichmentSections?.has(enrichmentObjecttypeId),
            ontoggle: () =>
              groupedEnrichment.dispatchEvent(new CustomEvent('enrichment-details-toggle', { bubbles: true })),
            'data-attribute-group-id': enrichmentObjecttypeId,
          },
          [
            h('summary.d-stack-h.space-0_25.enrichment-group-tile--summary', groupEnrichmentSummaryChildren),
            enrichedAttributeList,
          ],
        );
        formGroupContent.push(groupedEnrichment);
      } else {
        enrichedAttributeList.setAttribute('aria-labelledby', labelId);
        formGroupContent.push(enrichedAttributeList);
      }
    }
  }

  return h('.form-group', formGroupContent);
}

function createGroupEnrichmentSummaryChildren(
  enrichmentPrintName: string,
  enrichmentObjecttypeId: ObjecttypeIdString,
  options: CreateAttributeListGroupedByCategoryOptions,
) {
  const clickableLabel = h(
    // div needed for wrapping text around label and making margin-right work (avoiding 'important' property from base css)
    '.d-details-clickable-label--container',
    [
      createResourceLabel('enrichment', enrichmentPrintName, {
        styleClass: ['d-enrichment-resource-label', 'ellipsis'],
      }),
    ],
  );
  const moreActionsButton = createGroupedEnrichmentMoreActionsButton({
    enrichmentPrintName,
    enrichmentObjecttypeId,
    getEnrichmentActions: options.getEnrichmentActions,
  });
  const groupedEnrichmentSummaryChildren = [clickableLabel, moreActionsButton];
  if (options.outdatedEnrichmentsContext?.isOutdated(enrichmentObjecttypeId)) {
    groupedEnrichmentSummaryChildren.push(
      createWarningButton(enrichmentObjecttypeId, options.outdatedEnrichmentsContext),
    );
  }
  return groupedEnrichmentSummaryChildren;
}

function createGroupedEnrichmentMoreActionsButton({
  getEnrichmentActions,
  enrichmentObjecttypeId,
  enrichmentPrintName,
}: {
  enrichmentPrintName: string;
  enrichmentObjecttypeId: ObjecttypeIdString;
  getEnrichmentActions?: (enrichmentObjecttypeId: ObjecttypeIdString) => ReturnType<MenuContent>;
}) {
  if (getEnrichmentActions && isFeatureAvailable('OBJECTTYPE_ENRICHMENT')) {
    const moreActionsButton = createMoreActionsButton(enrichmentPrintName, { size: 's' });
    setupMenuButton(moreActionsButton, () => getEnrichmentActions(enrichmentObjecttypeId));
    return moreActionsButton;
  }
  return;
}

function createWarningButton(
  enrichmentObjecttypeId: ObjecttypeIdString,
  outdatedEnrichmentsData: OutdatedEnrichmentsContext,
) {
  const button = createIconButton(icon(warningIcon), '', {
    size: 'xs',
    variant: 'borderless-warning',
  });
  const content = () => createEnrichmentOutdatedAlert(enrichmentObjecttypeId, outdatedEnrichmentsData);
  setupPopupButton(button, content, {
    arrow: true,
    placement: 'right-start',
    variant: ['info', 'padding-3'],
  });
  return button;
}

function renderAttributeItems(
  attributes: AttributePickerAttribute[],
  options: CreateAttributeListGroupedByCategoryOptions,
) {
  const createItem = (attr: AttributePickerAttribute) => createAttributeItem(attr, options);
  return renderAttributesAndHierarchies(attributes, createItem, createItem, options.getAttributeById);
}

function createAttributeItem(
  attribute: AttributeTileAttribute,
  {
    hoverContext,
    withManagementLink,
    getAttributeActions,
    acceptedKeywords,
    attributeTileOptions,
    attributeTileOptionsProvider,
  }: CreateAttributeListGroupedByCategoryOptions,
) {
  const tileSpecificOptions = attributeTileOptionsProvider ? attributeTileOptionsProvider(attribute) : {};
  const attributeItem = attributeTile(attribute, {
    noDetails: true,
    noLinkToManagementCenter: !withManagementLink,
    hoverContext,
    menu: getAttributeActions && (() => getAttributeActions(attribute.id)),
    ...attributeTileOptions,
    ...tileSpecificOptions,
  });
  attributeItem.mark(acceptedKeywords);
  return attributeItem;
}
